import { createContext, useContext, useEffect, useState } from 'react';

/**
 * LES PHOTOS DE REFERENCE DES FICHES, vues de `@taki/ui`
 * (`docs/socle/couleur-photo-fiche.md`).
 *
 * `@taki/ui` ne parle pas au SDK : il dessine. Ce contexte est la couture. L'hote
 * (`@taki/app-runtime`, `photos.ts`) le fournit autour du `ViewHost`, avec le SDK
 * derriere ; la pastille, la liste, le tableau et le kanban n'en connaissent que
 * ces trois verbes.
 *
 * SANS FOURNISSEUR, PAS DE PHOTO : la pastille garde ses initiales, et le geste
 * « Changer la photo » n'est pas propose. C'est un fait, pas une panne : un ecran
 * monte hors d'un hote (un banc d'essai, une vue custom) ne ment pas.
 */

/** La fiche qui porte (ou recevra) la photo : sa famille et son id. */
export interface FicheDePhoto {
  collection: string;
  id: string;
}

export interface PhotosDeFichesPort {
  /**
   * L'URL d'objet de la photo (`blob:`), ou `null` quand elle ne se lit pas
   * (supprimee, droits, reseau). Jamais une URL de telechargement durable : le
   * fournisseur lit les octets et en fait un blob, qu'il garde par id.
   */
  charger(fileId: string): Promise<string | null>;
  /** Envoie la photo (deja reduite) et rend l'id du fichier cree. Leve si le serveur refuse. */
  envoyer(fiche: FicheDePhoto, fichier: File): Promise<string>;
  /** Supprime le fichier d'une photo qui n'est plus celle d'aucune fiche. */
  retirer(fileId: string): Promise<void>;
  /**
   * L'URL deja en memoire, sans attendre : une pastille qui se remonte (une
   * liste qui defile) ne repasse pas par les initiales. Facultatif.
   */
  enCache?(fileId: string): string | undefined;
}

/** Le contexte. `null` : pas de fournisseur, pas de photo. */
export const PhotosDeFiches = createContext<PhotosDeFichesPort | null>(null);

/** Le fournisseur courant, ou `null`. */
export function usePhotosDeFiches(): PhotosDeFichesPort | null {
  return useContext(PhotosDeFiches);
}

/**
 * L'URL de la photo d'une fiche, ou `undefined` : pas d'id, pas de fournisseur,
 * photo qui ne se lit pas, ou lecture en cours (les initiales tiennent la place).
 */
export function usePhotoDeFiche(fileId: string | undefined): string | undefined {
  const port = usePhotosDeFiches();
  const [lue, setLue] = useState<{ id: string; url: string | null } | null>(null);
  useEffect(() => {
    if (port === null || fileId === undefined) return undefined;
    let annule = false;
    port.charger(fileId).then(
      (url) => {
        if (!annule) setLue({ id: fileId, url });
      },
      () => {
        if (!annule) setLue({ id: fileId, url: null });
      },
    );
    return () => {
      annule = true;
    };
  }, [port, fileId]);
  if (port === null || fileId === undefined) return undefined;
  const enCache = port.enCache?.(fileId);
  if (enCache !== undefined) return enCache;
  return lue !== null && lue.id === fileId ? (lue.url ?? undefined) : undefined;
}
